/**
 * Error Logger Utility
 * 
 * Logs critical errors to Firestore for monitoring and alerting
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Log a critical error to Firestore
 * @param {string} errorType - Type of error (e.g., 'signup_failed', 'profile_creation_failed')
 * @param {Error|string} error - The error object or message
 * @param {Object} context - Additional context (userId, email, etc.)
 * @returns {Promise<string|null>} Document ID if successful, null otherwise
 */
export async function logCriticalError(errorType, error, context = {}) {
  try {
    const errorData = {
      errorType,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorCode: error?.code || null,
      errorStack: error instanceof Error ? error.stack : null,
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      },
      severity: 'critical',
      resolved: false,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'errorLogs'), errorData);
    
    if (process.env.NODE_ENV === 'development') {
      console.error(`[ErrorLogger] Critical error logged:`, errorData);
    }
    
    return docRef.id;
  } catch (logError) {
    // Fallback: log to console if Firestore logging fails
    console.error('[ErrorLogger] Failed to log error to Firestore:', logError);
    console.error('[ErrorLogger] Original error:', error);
    return null;
  }
}

/**
 * Log a signup failure
 * @param {Error} error - The error that occurred
 * @param {Object} context - Context (userId, email, step, etc.)
 */
export async function logSignupError(error, context = {}) {
  return logCriticalError('signup_failed', error, {
    ...context,
    category: 'authentication',
  });
}

/**
 * Log a profile creation failure
 * @param {Error} error - The error that occurred
 * @param {Object} context - Context (userId, email, profileData, etc.)
 */
export async function logProfileCreationError(error, context = {}) {
  return logCriticalError('profile_creation_failed', error, {
    ...context,
    category: 'profile',
  });
}
